/**
 * Utility functions for color manipulation and contrast calculations
 */

/**
 * Converts a hex color to RGB components
 * @param hex - Hex color string (e.g., #RRGGBB or #RGB)
 * @returns RGB values as array [r, g, b]
 */
export const hexToRgb = (hex: string): [number, number, number] => {
  // Remove # if present
  hex = hex.replace(/^#/, '');
  
  // Handle short hex format (e.g. #FFF)
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }
  
  // Parse hex to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  return [r, g, b];
};

/**
 * Calculates the relative luminance of a color
 * Uses the formula from WCAG 2.0: https://www.w3.org/TR/WCAG20-TECHS/G17.html
 * @param hex - Hex color string
 * @returns Luminance value between 0 (black) and 1 (white)
 */
export const getLuminance = (hex: string): number => {
  const [r, g, b] = hexToRgb(hex);
  
  // Convert RGB to relative luminance
  const [rs, gs, bs] = [r, g, b].map(c => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  
  // WCAG luminance formula 
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/**
 * Calculates contrast ratio between two colors
 * @param color1 - First hex color
 * @param color2 - Second hex color
 * @returns Contrast ratio (1:1 to 21:1)
 */
export const getContrastRatio = (color1: string, color2: string): number => {
  const lum1 = getLuminance(color1);
  const lum2 = getLuminance(color2);
  
  // WCAG contrast ratio formula
  return (Math.max(lum1, lum2) + 0.05) / (Math.min(lum1, lum2) + 0.05);
};

/**
 * Determines whether white or black text will provide better contrast
 * @param backgroundColor - Hex color for the background
 * @returns Contrasting text color (#FFFFFF or #000000)
 */
export const getContrastTextColor = (backgroundColor: string): string => {
  const luminance = getLuminance(backgroundColor);
  // Use white text on dark backgrounds, black on light backgrounds
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

/**
 * Creates text CSS variables based on background color
 * @param backgroundColor - Hex color for the background
 * @param primaryColor - Hex color for primary elements
 * @returns Object with CSS variables for text colors
 */
export const generateTextColors = (backgroundColor: string, primaryColor: string): {
  textColor: string;
  primaryTextColor: string;
} => {
  return {
    textColor: getContrastTextColor(backgroundColor),
    primaryTextColor: getContrastTextColor(primaryColor)
  };
}; 